import type { AwxConnectedAccountType } from '@ankor-io/common/types/awx'
import type {
  ModuleAvailabilityPayload,
  Vessel,
  VesselAndConnectedAccounts,
  VesselConnectedAccountAccess,
} from '@ankor-io/common/types/pay'

import { type HttpConfig, http } from '@/api/client/http'
import { createOrganizationPaths, createVesselPaths } from '@/api/paths'

type ListVesselsParams = {
  take?: number
  skip?: number
}

/** Same shape as CreateVesselSchema in pay-endpoint create.vessel.handler */
type CreateVesselBody = {
  name: string
  type?: AwxConnectedAccountType
  entityId?: string
}

export const vesselService = {
  /**
   * Create a vessel in the organization (provisions a connected account).
   * Backend returns the vessel object directly; use getVesselAndConnectedAccounts to get connected account id.
   */
  createVessel(orgId: string, body: CreateVesselBody, config?: HttpConfig): Promise<Vessel> {
    const paths = createOrganizationPaths(orgId)
    return http.post<Vessel>(paths.vessels(), body, config)
  },

  /**
   * List vessels the user has access to within an organization.
   */
  listOrganizationVessels(
    orgId: string,
    params?: ListVesselsParams,
    config?: HttpConfig,
  ): Promise<{ vessels: VesselConnectedAccountAccess[] }> {
    const paths = createOrganizationPaths(orgId)
    const url = paths.vessels()
    const searchParams = new URLSearchParams()
    if (params?.take !== undefined) {
      searchParams.set('take', String(params.take))
    }
    if (params?.skip !== undefined) {
      searchParams.set('skip', String(params.skip))
    }
    const query = searchParams.toString()
    return http.get<{ vessels: VesselConnectedAccountAccess[] }>(query ? `${url}?${query}` : url, config)
  },

  /**
   * Get a vessel and its connected accounts.
   */
  getVesselAndConnectedAccounts(
    orgId: string,
    vesselId: string,
    config?: HttpConfig,
  ): Promise<{ vesselAndConnectedAccounts: VesselAndConnectedAccounts }> {
    const paths = createOrganizationPaths(orgId)
    const url = paths.vesselConnectedAccounts(vesselId)
    return http.get<{ vesselAndConnectedAccounts: VesselAndConnectedAccounts }>(url, config)
  },

  /**
   * Get module availability and enablement status for a vessel.
   */
  getModuleAvailability(orgId: string, vesselId: string, config?: HttpConfig): Promise<ModuleAvailabilityPayload> {
    const paths = createOrganizationPaths(orgId)
    const url = paths.vesselConnectedAccountsAvailability(vesselId)
    return http.get<ModuleAvailabilityPayload>(url, config)
  },

  /**
   * Activate a module for a vessel.
   * Backend automatically determines which module to activate based on what's already active.
   * Backend gets accountId from authenticated user context.
   * @returns connectedAccountId string
   */
  activateModule(orgId: string, vesselId: string, config?: HttpConfig): Promise<string> {
    const paths = createVesselPaths(orgId, vesselId)
    const url = paths.activateModule()
    return http.post<string>(url, {}, config)
  },
}
